import React, { useCallback, useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { RefreshCw } from "lucide-react";
import { getProductFeedTargetConnections, getProductFeedTargetEnvironments } from "../../../utils/APIs";
import { Field, IconButton, optionsTip, OutcomeBadge, Section, SelectInput, Stat } from "./common";
import { DELIVERY_LABELS, ZERO_INVENTORY_LABELS } from "./constants";
import { GLOSSARY } from "./glossary";
import { errorMessage, formatDate, formatDuration } from "./helpers";

const POLL_MS = 5000;
const SETUP_PATH = "/setup/adobeIO-integrations";

const METHOD_TIP = optionsTip("How Adobe Target gets the products.", GLOSSARY.delivery.methods, DELIVERY_LABELS);
const PUSH_STATUS_TIP = optionsTip("The last push to Adobe Target.", GLOSSARY.delivery.pushStatus);
const ZERO_INVENTORY_TIP = optionsTip("What each build does with products whose inventory is 0.", GLOSSARY.delivery.zeroInventory, ZERO_INVENTORY_LABELS);
const ZERO_INVENTORY_HELP = {
    send: "Saved with inventory 0 on every build, so they stay in Target while your sources list them. Add the inventory > 0 rule to your criteria.",
    delete: "Deleted from this environment on every build until they're back in stock.",
    skip: "Left out of the push. Target keeps what it last received for them, possibly in stock, for up to 61 days.",
};

const MethodOption = ({ name, value, checked, disabled, comingSoon, onSelect, children }) => {
    const off = disabled || comingSoon;
    return (
        <label
            className={`flex-1 min-w-[240px] flex gap-3 items-start p-3 rounded-lg border ${checked ? "border-primary bg-blue-50/40" : "border-gray-200"} ${off ? "opacity-60 cursor-not-allowed bg-gray-50" : "cursor-pointer hover:border-primary"}`}
        >
            <input type="radio" name={name} className="mt-1" value={value} checked={checked} disabled={off} onChange={() => onSelect(value)} />
            <div className="flex flex-col gap-1 min-w-0">
                <span className="text-sm font-medium text-gray-900 flex flex-wrap items-center gap-2">
                    {DELIVERY_LABELS[value]}
                    {comingSoon && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-red-600 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Coming soon</span>
                    )}
                </span>
                {children}
            </div>
        </label>
    );
};

// Numbers from the last push, in the order people read them.
const pushStats = (stats) => [
    ["Saved", stats.saved, GLOSSARY.delivery.counts.saved],
    ["0 inventory", stats.zero_inventory, GLOSSARY.delivery.counts.zeroInventory],
    ["Deleted", stats.deleted, GLOSSARY.delivery.counts.deleted],
    ["Not sent", stats.skipped, GLOSSARY.delivery.counts.skipped],
    ["Rejected", stats.rejected, GLOSSARY.delivery.counts.rejected],
];

/**
 * Last push, its numbers and why things weren't saved. Shared with the run details.
 */
export const PushResult = ({ push }) => {
    if (!push) return null;
    const stats = push.stats;
    return (
        <div className="flex flex-col gap-2">
            <p className={`text-sm ${push.status === "failed" ? "text-red-600" : push.status === "partial" ? "text-yellow-700" : "text-gray-700"}`}>{push.message}</p>
            {stats && (
                <div className="flex flex-wrap gap-2">
                    {pushStats(stats).map(([label, value, tip]) => (
                        <Stat key={label} label={label} value={value ?? 0} tip={tip} />
                    ))}
                </div>
            )}
            {stats?.reject_causes?.length > 0 && (
                <div className="text-sm">
                    <p className="font-medium text-gray-700">Rejected by Target</p>
                    {stats.reject_causes.map((cause, i) => (
                        <p key={i} className="text-yellow-700">{cause.text} <span className="text-gray-500">({cause.occurrences})</span></p>
                    ))}
                </div>
            )}
            {stats?.skipped_samples?.length > 0 && (
                <div className="text-sm">
                    <p className="font-medium text-gray-700">Not sent</p>
                    {stats.skipped_samples.map((row, i) => (
                        <p key={i} className="text-yellow-700"><span className="font-mono">{row.id || "(no id)"}</span>: {row.reason}</p>
                    ))}
                </div>
            )}
            {stats?.errors?.length > 1 && (
                <div className="text-sm text-red-600">
                    {stats.errors.slice(1).map((error, i) => <p key={i}>{error}</p>)}
                </div>
            )}
        </div>
    );
};

// Delivery settings (draft) and the live push status for feeds pushed through
// the Adobe Target API.
const DeliverySection = ({ feed, settings, setSetting, auth, csvEnabled, reload }) => {
    const [connections, setConnections] = useState(null);
    const [environments, setEnvironments] = useState({ list: [], loading: false, error: "" });
    const radioName = useId();

    const method = settings.delivery?.method || "url";
    const target = settings.delivery?.target || {};
    const integrationId = target.integration_id || "";
    const zeroInventory = target.zero_inventory || "send";
    const setTarget = (patch) => setSetting(["delivery", "target"], { zero_inventory: "send", ...target, ...patch });

    useEffect(() => {
        getProductFeedTargetConnections(auth)
            .then((result) => setConnections(result?.data || []))
            .catch((e) => {
                setConnections([]);
                toast.error(errorMessage(e, "Adobe Target connections couldn't be loaded."));
            });
    }, [auth.token, auth.organizationId]);

    const loadEnvironments = useCallback(async (id) => {
        if (!id) {
            setEnvironments({ list: [], loading: false, error: "" });
            return;
        }
        setEnvironments((current) => ({ ...current, loading: true, error: "" }));
        try {
            const result = await getProductFeedTargetEnvironments({ ...auth, integrationId: id });
            setEnvironments({ list: result?.data || [], loading: false, error: "" });
        } catch (e) {
            setEnvironments({ list: [], loading: false, error: errorMessage(e, "Target environments couldn't be loaded.") });
        }
    }, [auth.token, auth.organizationId]);

    useEffect(() => {
        if (method === "api") loadEnvironments(integrationId);
    }, [method, integrationId, loadEnvironments]);

    // The editor refreshes while a build runs; this covers a push that outlives it.
    const pushRunning = feed.delivery?.status === "running";
    useEffect(() => {
        if (!pushRunning) return undefined;
        const timer = setInterval(reload, POLL_MS);
        return () => clearInterval(timer);
    }, [pushRunning, reload]);

    const selectMethod = (value) => {
        if (value === "api" && !integrationId) {
            const preferred = connections?.find((c) => c.default) || connections?.[0];
            setSetting(["delivery"], { ...(settings.delivery || {}), method: value, target: { zero_inventory: "send", ...target, integration_id: preferred?._id || null, environment_id: null } });
            return;
        }
        setSetting(["delivery", "method"], value);
    };

    const selectConnection = (id) => setTarget({ integration_id: id || null, environment_id: null });

    const noConnections = connections !== null && connections.length === 0;
    const connection = connections?.find((c) => c._id === integrationId);
    const environmentOptions = environments.list.map((env) => ({ value: String(env.id), label: `${env.name}${env.default ? " (default)" : ""} · ${env.id}` }));
    if (target.environment_id && !environments.list.some((env) => String(env.id) === String(target.environment_id))) {
        environmentOptions.unshift({ value: String(target.environment_id), label: `Environment ${target.environment_id}` });
    }
    const publishedApi = feed.delivery_method === "api";
    const delivery = feed.delivery;

    return (
        <>
            <Section title="Delivery" description="How Adobe Target gets this feed's products. Changes apply when you publish." tip={METHOD_TIP}>
                <div className="flex flex-wrap gap-3">
                    <MethodOption name={radioName} value="api" checked={method === "api"} disabled={noConnections && method !== "api"} onSelect={selectMethod}>
                        <span className="text-xs text-gray-500">After every successful build Stride saves every product straight into Target through the Recommendations API. No Target feed needed.</span>
                        {noConnections && (
                            <span className="text-xs text-yellow-700">
                                Needs an Adobe IO integration with Adobe Target enabled. <Link to={SETUP_PATH} className="text-primary hover:underline">Set one up</Link>.
                            </span>
                        )}
                    </MethodOption>
                    <MethodOption name={radioName} value="url" checked={method === "url"} comingSoon={!csvEnabled} onSelect={selectMethod}>
                        <span className="text-xs text-gray-500">Stride hosts the CSV at a secret URL; a URL feed in Target imports it daily, weekly or every two weeks.</span>
                    </MethodOption>
                </div>

                {method === "api" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Adobe Target connection" tip={GLOSSARY.delivery.connection} help={connection ? `Tenant: ${connection.tenant}` : undefined}>
                            <SelectInput
                                value={integrationId}
                                placeholder={connections === null ? "Loading…" : "Choose a connection"}
                                options={[
                                    ...(connections || []).map((c) => ({ value: c._id, label: `${c.name || c.tenant}${c.default ? " (default)" : ""}` })),
                                    ...(integrationId && connections && !connection ? [{ value: integrationId, label: "Missing integration" }] : []),
                                ]}
                                onChange={selectConnection}
                            />
                        </Field>
                        <Field
                            label="Target environment"
                            tip={GLOSSARY.delivery.environment}
                            help={environments.error || (environments.loading ? "Loading environments…" : undefined)}
                        >
                            <div className="flex items-center gap-2">
                                <SelectInput
                                    value={target.environment_id ? String(target.environment_id) : ""}
                                    placeholder="Choose an environment"
                                    disabled={!integrationId}
                                    options={environmentOptions}
                                    onChange={(value) => setTarget({ environment_id: value ? Number(value) : null })}
                                />
                                <IconButton title="Reload environments" disabled={!integrationId || environments.loading} onClick={() => loadEnvironments(integrationId)}>
                                    <RefreshCw size={16} className={environments.loading ? "animate-spin" : ""} />
                                </IconButton>
                            </div>
                        </Field>
                        <Field label="Products with 0 inventory" tip={ZERO_INVENTORY_TIP} help={ZERO_INVENTORY_HELP[zeroInventory]}>
                            <SelectInput
                                value={zeroInventory}
                                options={["send", "delete", "skip"].map((value) => ({ value, label: ZERO_INVENTORY_LABELS[value] }))}
                                onChange={(value) => setTarget({ zero_inventory: value })}
                            />
                        </Field>
                        <p className="md:col-span-2 text-xs text-gray-500">
                            Products that disappear from your sources can't be deleted, because Stride doesn't keep a list of what it sent. Target drops them 61 days after they were last pushed.
                        </p>
                        {environments.error && <p className="md:col-span-2 text-xs text-red-600">{environments.error}</p>}
                    </div>
                )}

                {csvEnabled && method === "api" && feed.is_published && !publishedApi && (
                    <p className="text-xs text-yellow-700">
                        Once published, the feed URL stops working. If Target has a URL feed for it, delete or deactivate that feed in Target so its imports don't fail.
                    </p>
                )}
                {!csvEnabled && method === "url" && (
                    <p className="text-xs text-red-600">
                        This feed is set to deliver by feed URL, which isn't available yet. Switch it to the Adobe Target API to publish.
                    </p>
                )}
                {csvEnabled && method === "url" && publishedApi && (
                    <p className="text-xs text-yellow-700">
                        Once published, Stride stops pushing and the feed URL works again. Create a URL feed in Target for it; products already pushed stay in Target until the feed updates or they expire.
                    </p>
                )}
            </Section>

            {publishedApi && (
                <Section
                    title="Adobe Target push"
                    tip={PUSH_STATUS_TIP}
                    description="Every successful build pushes every product. To push again sooner, use Run now on the Runs tab."
                >
                    {delivery ? (
                        <>
                            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-gray-500">
                                <span className="flex items-center gap-1">Last push <OutcomeBadge outcome={delivery.status} /> {formatDate(delivery.finished_at || delivery.started_at)}</span>
                                {delivery.duration_ms !== undefined && <span>Took {formatDuration(delivery.duration_ms)}</span>}
                                {delivery.tenant && <span>Tenant {delivery.tenant}</span>}
                                {delivery.environment_id && <span>Environment {delivery.environment_id}</span>}
                                <span>Last successful push: {formatDate(delivery.last_success_at)}</span>
                            </div>
                            {!pushRunning && <PushResult push={delivery} />}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500">Nothing pushed yet. The next build pushes every product.</p>
                    )}
                </Section>
            )}

        </>
    );
};

export default DeliverySection;
